// Sammendrag — meldt, faktisk og avvik, per person og per aktivitetstype.
//
// AVVIKET SKAL FORKLARES, IKKE BARE VISES. Et tall i en kolonne som heter
// «avvik» sier ingenting til den som ikke allerede vet hva det er. Derfor har
// hver rad med avvik en setning som sier hva de to tallene er og hvorfor de
// skiller lag — og hvorfor verktøyet ikke har «ryddet opp» i det selv.
//
// Ingen av tallene regnes her. Tjenesten regner dem med @skade/dugnad, som
// runder ÉN gang. Klienten viser.
import type { Kontekst } from './kontekst.js';
import {
  lesSammendrag, sorterPåAvvik, tolkAvvik, type PersonRad, type Sammendrag, type TypeRad,
} from '../lib/sammendrag-lib.js';
import { formaterDatoMedÅr, lokaleDeler, somDatoFelt, tolkØktSkjema } from '../lib/tid.js';
import { div, felt, h, knapp, merke, p, sett, span } from '../ui/dom.js';
import { ikon } from '../ui/ikoner.js';
import { feilboks, laster, søyle, tomt } from '../ui/tilstand.js';

export function visSammendrag(rot: HTMLElement, ctx: Kontekst): void {
  const nå = Date.now();
  let data: Sammendrag | null = null;

  const side = div('side');
  sett(rot, side);

  const topp = div('side-topp');
  const titler = div('');
  titler.append(h(1, 'side-tittel', 'Sammendrag'));
  titler.append(p('side-under', 'Meldt tid er summen av alle økter. Faktisk tid teller '
    + 'overlappende timer én gang. Differansen er tid som er ført to ganger.'));
  topp.append(titler);

  const d = lokaleDeler(nå);
  const fra = felt('Fra', { type: 'date', verdi: `${d.aar}-01-01` });
  const til = felt('Til', { type: 'date', verdi: somDatoFelt(nå) });
  const filterrad = div('filterrad');
  filterrad.append(fra.rot, til.rot);
  filterrad.append(knapp('Oppdater', () => void hent(), { variant: 'stille', ikon: ikon('last') }));
  topp.append(filterrad);
  side.append(topp);

  const innhold = div('');
  side.append(innhold);

  void hent();

  async function hent(): Promise<void> {
    sett(innhold, laster('Regner sammen …', 4));
    const f = dagStart(fra.input.value);
    const t = dagSlutt(til.input.value);
    const r = await ctx.klient.sammendrag(
      Number.isFinite(f) ? f : undefined,
      Number.isFinite(t) ? t : undefined,
    );
    if (!r.ok) return sett(innhold, feilboks(r.feil, () => void hent()));
    data = lesSammendrag(r.data);
    tegn();
  }

  function tegn(): void {
    if (!data) return;
    sett(innhold);

    if (!data.antallOkter) {
      innhold.append(tomt('Ingen økter i perioden',
        'Ingen meldte eller godkjente økter mellom datoene over. Utvid perioden, '
        + 'eller meld inn timer under «Timer».',
        { ikon: 'sammendrag', handling: { tekst: 'Til timer', gjør: () => ctx.gåTil('timer') } }));
      return;
    }

    innhold.append(totalkort(data));
    if (data.utelatt) innhold.append(utelattlinje(data));
    innhold.append(typekort(data.perType));
    innhold.append(personkort(data.personer));
  }

  // ══ Totalen ═══════════════════════════════════════════════════════════
  function totalkort(s: Sammendrag): HTMLElement {
    const avvik = tolkAvvik(s.totalt.meldtMinutter, s.totalt.sammenslattMinutter);
    const kort = div('kort');

    const hode = div('kort-hode');
    hode.append(ikon('sammendrag'), h(2, 'kort-tittel', 'Hele perioden'));
    if (s.fra !== null || s.til !== null) {
      hode.append(span('kort-hode-under',
        `${s.fra !== null ? formaterDatoMedÅr(s.fra) : 'start'} – ${s.til !== null ? formaterDatoMedÅr(s.til) : 'nå'}`));
    }
    kort.append(hode);

    const bokser = div('sumkort');
    bokser.append(tallboks('Meldt tid', s.totalt.meldtTekst, `${s.totalt.meldtTimer} t · summen av alle økter`));
    bokser.append(tallboks('Faktisk tid', s.totalt.sammenslattTekst,
      `${s.totalt.sammenslattTimer} t · hver time talt én gang`));
    bokser.append(tallboks('Avvik', avvik.harAvvik ? avvik.merkelapp : '0 min',
      avvik.harAvvik ? 'tid ført på to økter' : 'ingen overlapp'));
    bokser.append(tallboks('Økter', String(s.antallOkter), `${s.personer.length} personer`));
    kort.append(bokser);

    kort.append(avviksforklaring(avvik));
    return kort;
  }

  function tallboks(etikett: string, stor: string, under: string): HTMLElement {
    const b = div('sumboks');
    b.append(span('sumboks-etikett', etikett));
    b.append(span('sumboks-tall', stor));
    b.append(span('sumboks-under', under));
    return b;
  }

  function utelattlinje(s: Sammendrag): HTMLElement {
    const rad = div('utelatt');
    rad.append(ikon('info'));
    rad.append(span('', `${s.utelatt} ${s.utelatt === 1 ? 'økt er' : 'økter er'} ikke med i summene.`
      + (s.utelattForklaring ? ` ${s.utelattForklaring}.` : '')));
    return rad;
  }

  /** Setningen som gjør avviket til noe man kan handle på. */
  function avviksforklaring(a: ReturnType<typeof tolkAvvik>): HTMLElement {
    const boks = div(`avviksboks${a.harAvvik ? ' har-avvik' : ''}`);
    boks.append(ikon(a.harAvvik ? 'varsel' : 'sjekk'));
    boks.append(p('', a.forklaring));
    return boks;
  }

  // ══ Per aktivitetstype ════════════════════════════════════════════════
  function typekort(rader: readonly TypeRad[]): HTMLElement {
    const kort = div('kort');
    const hode = div('kort-hode');
    hode.append(ikon('kanban'), h(2, 'kort-tittel', 'Per aktivitetstype'));
    kort.append(hode);

    const størst = Math.max(1, ...rader.map((r) => r.meldtMinutter));
    for (const r of rader) {
      const rad = div('typerad');
      const topp2 = div('typerad-topp');
      topp2.append(span('typerad-navn', r.navn));
      topp2.append(span('typerad-tall', r.meldtTekst));
      rad.append(topp2);
      rad.append(søyle(r.meldtMinutter / størst, 'tid', `${r.navn}: ${r.meldtTekst} meldt`));
      const under = div('typerad-under');
      under.append(span('', `${r.antall} ${r.antall === 1 ? 'økt' : 'økter'}`));
      if (r.avvikMinutter > 0) {
        under.append(merke(tolkAvvik(r.meldtMinutter, r.sammenslattMinutter).merkelapp, 'gull'));
      }
      rad.append(under);
      kort.append(rad);
    }
    return kort;
  }

  // ══ Per person ════════════════════════════════════════════════════════
  function personkort(personer: readonly PersonRad[]): HTMLElement {
    const kort = div('kort');
    const hode = div('kort-hode');
    hode.append(ikon('person'), h(2, 'kort-tittel', 'Per person'));
    hode.append(span('kort-hode-under', 'Pseudonymer. Navn og ansattnummer vises aldri her.'));
    kort.append(hode);

    for (const pr of sorterPåAvvik(personer)) kort.append(personrad(pr));
    return kort;
  }

  function personrad(pr: PersonRad): HTMLElement {
    const avvik = tolkAvvik(pr.meldtMinutter, pr.sammenslattMinutter);
    const rad = div('personrad');

    const topp2 = div('personrad-topp');
    const navn = span('personrad-id', pr.personId);
    if (pr.personId === ctx.meg.person.id) navn.append(merke('deg', 'gull'));
    topp2.append(navn);
    topp2.append(span('personrad-tall', `${pr.meldtTekst} meldt`));
    rad.append(topp2);

    const tall = div('personrad-tall-rad');
    tall.append(nøkkeltall('Meldt', pr.meldtTekst));
    tall.append(nøkkeltall('Faktisk', pr.sammenslattTekst));
    tall.append(nøkkeltall('Avvik', avvik.harAvvik ? avvik.merkelapp : '—'));
    tall.append(nøkkeltall('Økter', String(pr.antall)));
    rad.append(tall);

    if (avvik.harAvvik) {
      const f = div('personrad-forklaring');
      f.append(ikon('varsel'));
      f.append(span('', avvik.forklaring));
      rad.append(f);
    }

    if (pr.perType.length) {
      const typer = div('personrad-typer');
      for (const t of pr.perType) {
        typer.append(merke(`${t.navn}: ${t.meldtTekst}`));
      }
      rad.append(typer);
    }
    const koder = pr.perOppdragskode.filter((k) => k.oppdragskode !== '(ingen)');
    if (koder.length) {
      const kr = div('personrad-typer');
      for (const k of koder) kr.append(merke(`Kode ${k.oppdragskode}: ${k.meldtTekst}`, 'sage'));
      rad.append(kr);
    }
    return rad;
  }

  function nøkkeltall(etikett: string, verdi: string): HTMLElement {
    const b = div('nøkkeltall');
    b.append(span('nøkkeltall-etikett', etikett));
    b.append(span('nøkkeltall-verdi', verdi));
    return b;
  }
}

function dagStart(dato: string): number {
  const r = tolkØktSkjema(dato, '00:00', '00:01');
  return r.ok ? r.verdi.startTsMs : NaN;
}
function dagSlutt(dato: string): number {
  const r = tolkØktSkjema(dato, '23:59', '00:00');
  return r.ok ? r.verdi.startTsMs + 60_000 : NaN;
}
